import { Controller } from "@hotwired/stimulus"
import imageCompression from "browser-image-compression"

// Resize + re-encode images in the browser before upload.
//
// Usage:
//   <%= file_field :reference_picture, direct_upload: true,
//         data: { controller: "image-compress", action: "change->image-compress#compress" } %>
//
// Why: phone photos are commonly 6000×4000 / 8–12MB. AI image APIs downsample
// to ~2048px anyway, so we lose nothing by capping client-side. Combined with
// Active Storage `direct_upload: true`, the file goes straight from browser
// to Cloudinary at ~400KB instead of routing 10MB through the Rails dyno.
const OPTIONS = {
  maxSizeMB:          1.5,    // hard cap; quality auto-adjusts to hit it
  maxWidthOrHeight:   2048,   // long-edge cap — matches AI input resolution
  useWebWorker:       true,
  fileType:           "image/jpeg",
  initialQuality:     0.85,
  alwaysKeepResolution: false
}

export default class extends Controller {
  async compress(event) {
    const input = event.target
    if (!input.files || input.files.length === 0) return

    const compressed = []
    let bytesIn = 0
    let bytesOut = 0
    const t0 = performance.now()

    input.disabled = true
    input.dataset.originalTitle = input.title
    input.title = "Compressing…"

    try {
      for (const file of input.files) {
        // Skip non-images (PDFs, etc.) and tiny files (<300KB) — already cheap.
        if (!file.type.startsWith("image/") || file.size < 300 * 1024) {
          compressed.push(file)
          bytesIn  += file.size
          bytesOut += file.size
          continue
        }

        bytesIn += file.size
        const blob = await imageCompression(file, OPTIONS)
        // Preserve the original filename and treat as a real File for FormData.
        const out = new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), {
          type:         "image/jpeg",
          lastModified: Date.now()
        })
        bytesOut += out.size
        compressed.push(out)
      }

      // Replace the input's FileList with the compressed versions so any
      // subsequent code (Active Storage's direct upload, plain multipart
      // submit, etc.) picks up the smaller files.
      const dt = new DataTransfer()
      compressed.forEach(f => dt.items.add(f))
      input.files = dt.files

      const dt_ms = (performance.now() - t0).toFixed(0)
      const ratio = bytesIn > 0 ? (bytesOut / bytesIn * 100).toFixed(0) : 100
      console.log(`[image-compress] ${input.name}: ${(bytesIn/1024).toFixed(0)}KB → ${(bytesOut/1024).toFixed(0)}KB (${ratio}%, ${dt_ms}ms)`)
    } catch (err) {
      console.error("[image-compress] failed, falling back to original:", err)
    } finally {
      input.disabled = false
      input.title = input.dataset.originalTitle || ""
    }
  }
}
